import { Link } from 'react-router-dom';
import { useForm } from "react-hook-form";
import { yupResolver } from '@hookform/resolvers/yup';
import * as Yup from 'yup';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { useState } from 'react';
import { authActions } from '_store';
import { useMsal } from "@azure/msal-react";
import { loginRequest } from '../authConfig';
import { GoogleLogin } from '@react-oauth/google';
import axios from 'axios';
// eslint-disable-next-line
import UAVSphere_logo from "../assets/images/UAVspherelogo.png";

export { Login };

function Login() {
    const { instance } = useMsal();
    const dispatch = useDispatch();
    const navigate = useNavigate(); 
    // eslint-disable-next-line
    const [emailSent, setEmailSent] = useState(false);
    const [sending, setSending] = useState(false);
    const authError = useSelector(state => state.auth.error);
    const [microsoftLoading, setMicrosoftLoading] = useState(false);
    const [microsoftError, setMicrosoftError] = useState(null);

    // form validation rules 
    const validationSchema = Yup.object().shape({
        email: Yup.string().required('Username is required'),
        password: Yup.string().required('Password is required')
    });
    const formOptions = { resolver: yupResolver(validationSchema) };

    // get functions to build form with useForm() hook
    const { register, handleSubmit, formState, getValues } = useForm(formOptions);
    const { errors, isSubmitting } = formState;
    const [error, setError] = useState(null);

    const handleGoogleSuccess = async (credentialResponse) => {
  try {
    const res = await axios.post(`${process.env.REACT_APP_API_URL}/auth/google`, { 
      token: credentialResponse.credential 
    });

    if (res.status === 200) {
      const authData = {
        token: res.data.token,
        // We use res.data.profile because that's what we named it in the Python fix above
        user: res.data.profile, 
        master_password_used: res.data.master_password_used,
      };

      localStorage.setItem('auth', JSON.stringify(authData));
      localStorage.setItem('user', JSON.stringify(res.data.profile)); 
      window.dispatchEvent(new Event('storage'));

      dispatch(authActions.setAuth(authData));
      navigate("/home", { replace: true });
    }
  } catch (error) {
    setError(error.response?.data?.msg || "Google Login Failed");
  }
};


  const handleGoogleFailure = () => {
    setError('Google login failed');
  };

  const handleLinkedInLogin = () => {
    // Points directly to your Flask backend endpoint
    window.location.href = process.env.REACT_APP_LINKEDIN_LOGIN_URL
  };


const handleMicrosoftLogin = async () => {
    setMicrosoftLoading(true);
    setMicrosoftError(null); // Clear any previous errors

    try {
        // 1. Trigger the Microsoft Popup
        const loginResponse = await instance.loginPopup({
            ...loginRequest,
            prompt: "select_account"
        });

        const accessToken = loginResponse.accessToken;

        // 2. Send the token to your Flask backend
        const res = await axios.post(`${process.env.REACT_APP_API_URL}/auth/microsoft`, { 
            token: accessToken 
        });

        if (res.status === 200) {
            // 3. Structure the auth data exactly like Google
            const authData = {
                token: res.data.token,
                user: res.data.profile, 
                master_password_used: res.data.master_password_used,
            };

            // 4. Update LocalStorage so the <Route> auth check detects the user
            localStorage.setItem('auth', JSON.stringify(authData));
            localStorage.setItem('user', JSON.stringify(res.data.profile)); 

            // 5. Dispatch event for real-time storage sync
            window.dispatchEvent(new Event('storage'));

            // 6. Update Redux state using your setAuth reducer
            dispatch(authActions.setAuth(authData));

            // 7. Navigate to home
            navigate("/home", { replace: true });
        }
    } catch (error) {
        // Handle both Microsoft Popup errors and Backend errors
        const errorMsg = error.response?.data?.msg || error.message || "Microsoft Login Failed";
        setMicrosoftError(errorMsg);
        dispatch(authActions.setError(errorMsg));
    } finally {
        setMicrosoftLoading(false);
    }
};


    function onSubmit({ email, password }) {
        return dispatch(authActions.login({ email, password }));
    }

    function handleForgotPassword() {
        const email = getValues('email');
        setSending(true);

        dispatch(authActions.forgotPassword({ email }))
            .then(() => {
                setEmailSent(true);
                setSending(false);
            })
            .catch(() => {
                setSending(false);
            });
    }

    return (
    <div className="card m-3">
        <div className="text-center">
            <img src={require('../assets/images/UAVspherelogo.png')} alt="UAVSphere Logo" style={{ width: '150px' }} />
        </div>
        <h4 className="card-header">Login</h4>
        <br/>
        <div className="card-body">
            <form onSubmit={handleSubmit(onSubmit)}>
                <div className="mb-3">
                    <label className="form-label">Username</label>
                    <input name="email" type="text" {...register('email')} className={`form-control ${errors.email ? 'is-invalid' : ''}`} />
                    <div className="invalid-feedback">{errors.email?.message}</div>
                </div>
                <br/>
                <div className="mb-3">
                    <label className="form-label">Password</label>
                    <input name="password" type="password" {...register('password')} className={`form-control ${errors.password ? 'is-invalid' : ''}`} />
                    <div className="invalid-feedback">{errors.password?.message}</div>
                </div>
                <br/>
                
                <div className="auth-button-stack">
                    <button disabled={isSubmitting} className="btn btn-primary">
                        {isSubmitting && <span className="spinner-border spinner-border-sm me-1"></span>}
                        Login
                    </button>

                    <Link to="../register" className="btn btn-primary">Register</Link>

                    <button type="button" className="btn btn-secondary" onClick={handleForgotPassword} disabled={sending}>
                        {sending ? <span className="spinner-border spinner-border-sm me-1"></span> : 'Forgot Password?'}
                    </button>
                </div>
            </form>   
            {authError && <div className="error-message">{authError}</div>}             
        </div>
        
        <br/>
        
        {/* Social Login Buttons with Inline Styles */}
        <div style={{
            display: 'flex',
            flexDirection: 'column',
            gap: '12px',
            padding: '20px',
            maxWidth: '400px',
            margin: '0 auto',
            width: '100%'
        }}>
            {/* Google Button */}
            <div style={{
                display: 'flex',
                justifyContent: 'center',
                width: '100%'
            }}>
                <GoogleLogin
                    onSuccess={handleGoogleSuccess}
                    onError={handleGoogleFailure}
                    useOneTap
                    theme="outline"
                    size="large"
                    text="signin_with"
                    shape="rectangular"
                />
            </div>
            
            {/* LinkedIn Button */}
            <button 
                onClick={handleLinkedInLogin} 
                style={{
                    backgroundColor: '#0077b5',
                    color: 'white',
                    border: 'none',
                    borderRadius: '4px',
                    padding: '12px 24px',
                    fontSize: '16px',
                    fontWeight: '500',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '8px',
                    width: '100%',
                    transition: 'background-color 0.3s',
                    boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
                }}
                onMouseEnter={(e) => e.target.style.backgroundColor = '#006097'}
                onMouseLeave={(e) => e.target.style.backgroundColor = '#0077b5'}
            >
                <svg 
                    xmlns="http://www.w3.org/2000/svg" 
                    width="20" 
                    height="20" 
                    viewBox="0 0 24 24" 
                    fill="white"
                    style={{ marginRight: '8px' }}
                >
                    <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/>
                </svg>
                Sign in with LinkedIn
            </button>

            {/* Microsoft Button */}
            <button 
                onClick={handleMicrosoftLogin}
                disabled={microsoftLoading}
                style={{
                    backgroundColor: '#2f2f2f',
                    color: 'white',
                    border: 'none',
                    borderRadius: '4px',
                    padding: '12px 24px',
                    fontSize: '16px',
                    fontWeight: '500',
                    cursor: microsoftLoading ? 'not-allowed' : 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '8px',
                    width: '100%',
                    opacity: microsoftLoading ? 0.7 : 1,
                    transition: 'background-color 0.3s',
                    boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
                }}
                onMouseEnter={(e) => !microsoftLoading && (e.target.style.backgroundColor = '#1f1f1f')}
                onMouseLeave={(e) => !microsoftLoading && (e.target.style.backgroundColor = '#2f2f2f')}
            >
                {microsoftLoading ? (
                    <span>Logging in...</span>
                ) : (
                    <>
                        <svg 
                            xmlns="http://www.w3.org/2000/svg" 
                            width="20" 
                            height="20" 
                            viewBox="0 0 23 23"
                            style={{ marginRight: '8px' }}
                        >
                            <path fill="#f3f3f3" d="M0 0h23v23H0z"/>
                            <path fill="#f35325" d="M1 1h10v10H1z"/>
                            <path fill="#81bc06" d="M12 1h10v10H12z"/>
                            <path fill="#05a6f0" d="M1 12h10v10H1z"/>
                            <path fill="#ffba08" d="M12 12h10v10H12z"/>
                        </svg>
                        Continue with Microsoft
                    </>
                )}
            </button>
        </div>
        
        {error && <p style={{ color: 'red', textAlign: 'center', margin: '10px 0' }}>{error}</p>}
    </div>
)
}
